import { useCallback, useEffect, useRef, useState } from 'react';
import './control-boards.scss';
import DataGrid, { Toolbar, Item, SearchPanel, Selection, Column, Paging, LoadPanel as LoadPanelDg, Scrolling, Pager, GroupPanel, ColumnChooser, FilterRow } from 'devextreme-react/data-grid';
import { Button, LoadPanel, Tooltip } from 'devextreme-react';
import { useApiReports } from '../../api/api.reports';
import { FormPopupSimpleItem, ToolbarSimple } from '../../components';
import { FormField } from '../../types/types.system';
import { ScrollView } from 'devextreme-react';
import { filter, microscopieBlue } from '../../images/icons';
import { Export } from 'devextreme-react/circular-gauge';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver-es';
import { exportDataGrid } from 'devextreme/excel_exporter';

const dataFiltered: any = {};

const Index = () => {
  const [data, setData] = useState<any[]>([]);
  const { getControlsBoards, runBoardReport } = useApiReports();
  const [loading, setLoading] = useState(true);
  const [reportQuery, setReportQuery] = useState('');
  const [filtrosVisible, setFiltrosVisible] = useState<boolean>(false);
  const [filtros, setFiltros] = useState<FormField[]>([]);
  const [filtersApplied, setFiltersApplied] = useState<any>([]);
  const [sending, setSending] = useState<boolean>(false);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(true);
  const [visibleCount, setVisibleCount] = useState<number>(0);
  const gridRef = useRef<DataGrid>(null);

  useEffect(() => {
    setLoading(true);
    getControlsBoards([])
      .then((res) => {
        setData(res);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleClicSelection = useCallback(
    (e) => {
      const row = e.selectedRowsData && e.selectedRowsData[0];
      if (!row) return;
      // Clone the row to avoid accidental mutations from shared references
      try {
        const cloned = JSON.parse(JSON.stringify(row));
        setSelectedReport(cloned);
        setSelectedReportId(cloned.Rp_Id ?? null);
      } catch (err) {
        // Fallback to shallow copy if structured clone fails
        setSelectedReport({ ...row });
        setSelectedReportId(row.Rp_Id ?? null);
      }
      setReportQuery(row.Rp_Query);
      const filtros: FormField[] = JSON.parse(row.Rp_Filtros);
      setFiltros(filtros);
      setFiltrosVisible(true);
    },
    [setReportQuery]
  );

  const handleVisibleFilter = useCallback(() => {
    setFiltrosVisible(false);
  }, [setFiltrosVisible]);

  const handleClicFilter = useCallback(() => {
    const filters = filtros.map((field) => {
      console.log('Field:', filtros);
      let values = dataFiltered[field.equivalence];

      if (!Array.isArray(values)) {
        values = [values];
      }

      if (field.type === 'dxDateRangeBox' && values.length === 2) {
        const startDate = new Date(values[0]);
        const endDate = new Date(values[1]);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        values = [startDate, endDate];
      }

      return {
        Field: field.equivalence,
        Operator: field.typeCondition || '=',
        Values: values.filter((value) => value !== null),
      };
    });
    setFiltersApplied(filters);
    fetchData(filters);
  }, [filtros]);

  const fetchData = (filters) => {
    if (!filters || !reportQuery) return;
    runBoardReport(filters, reportQuery)
      .then((res) => {
        setFilteredData(res);
      })
      .finally(() => {
        setFiltrosVisible(false);
        setSending(false);
      });
  };

  const handleClicRefresh = useCallback(() => {
    fetchData(filtersApplied);
  }, [setFiltrosVisible, setSending, filtersApplied, reportQuery]);

  const onExporting = useCallback(
    (e) => {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('Main sheet');
      exportDataGrid({
        component: e.component,
        worksheet,
        autoFilterEnabled: true,
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `INFORME - ${selectedReport.Rp_Nombre}.xlsx`);
        });
      });
    },
    [selectedReport]
  );

  const toggleSidebar = useCallback(() => {
    setSidebarVisible((prev) => !prev);
  }, []);

  const getReportIcon = (name?: string) => {
    const n = (name || '').toLowerCase();
    if (n.includes('factura')) return 'money';
    if (n.includes('venta')) return 'cart';
    if (n.includes('estadist') || n.includes('total') || n.includes('estad')) return 'chart';
    // Use microscopie svg when report name includes examen/exámenes
    if (n.includes('examen') || n.includes('exámenes') || n.includes('examenes')) return 'microscopie';
    if (n.includes('informe') || n.includes('remit')) return 'doc';
    if (n.includes('vendedor') || n.includes('vended')) return 'group';
    if (n.includes('médico') || n.includes('medico') || n.includes('doctor')) return 'user';
    return 'doc';
  };

  const renderReportCell = (cellData: any) => {
    const name = cellData.data?.Rp_Nombre || '';
    const icon = getReportIcon(name);
    return (
      <div className='report-cell'>
        <div className='report-cell-icon' title={name}>
          {icon === 'microscopie' ? <img src={microscopieBlue} alt='microscopio' className='report-svg-icon' /> : <i className={`dx-icon dx-icon-${icon}`}></i>}
        </div>
        <div className='report-cell-name' title={name}>
          {name}
        </div>
      </div>
    );
  };

  const selectReport = useCallback((report: any) => {
    if (!report) return;
    // Clone to avoid holding a reference to the original data array item
    try {
      const cloned = JSON.parse(JSON.stringify(report));
      console.debug('[control-boards] selectReport - selecting (collapsed) id=', cloned.Rp_Id, 'name=', cloned.Rp_Nombre);
      setSelectedReport(cloned);
      setSelectedReportId(cloned.Rp_Id ?? null);
    } catch (err) {
      console.debug('[control-boards] selectReport - clone failed, using shallow copy', err);
      setSelectedReport({ ...report });
      setSelectedReportId(report.Rp_Id ?? null);
    }
    setReportQuery(report.Rp_Query);
    try {
      const f = JSON.parse(report.Rp_Filtros);
      setFiltros(f);
    } catch (e) {
      setFiltros([]);
    }
    // Do not auto-open filtros; just select the report in collapsed view
  }, []);

  return (
    <div className='control-boards-page'>
      <ScrollView className='view-wrapper-scroll'>
        <ToolbarSimple title='Tableros de control'>
          <div className='boards-container'>
            {/* Siempre renderizamos un panel izquierdo: completo o colapsado */}
            {sidebarVisible ? (
              <div className='boards-sidebar'>
                <div className='sidebar-toggle-header'>
                  <Button icon='chevronleft' onClick={toggleSidebar} stylingMode='text' hint='Ocultar panel' />
                </div>
                <LoadPanel visible={loading} position={{ of: '.boards-sidebar' }} />
                <DataGrid height={'calc(100vh - 250px)'} className='boards-grid' dataSource={data} keyExpr={'Rp_Id'} rowAlternationEnabled={false} hoverStateEnabled={true} onSelectionChanged={handleClicSelection} selectedRowKeys={selectedReportId ? [selectedReportId] : []}>
                  <Scrolling mode='virtual' />
                  <Selection mode='single' />
                  <Paging enabled={false} />
                  <Column dataField='Rp_Nombre' caption='TABLERO' cellRender={renderReportCell} />
                </DataGrid>
              </div>
            ) : (
              <div className='boards-sidebar-collapsed' role='navigation'>
                <div className='collapsed-inner'>
                  <div className='collapsed-toggle-top'>
                    <Button icon='menu' onClick={toggleSidebar} stylingMode='text' hint='Mostrar panel' />
                  </div>

                  <div className='collapsed-reports'>
                    {data && data.length > 0 ? (
                      data.map((rp: any, idx: number) => (
                        <button key={idx} type='button' className={`collapsed-report-item ${selectedReportId && selectedReportId === rp.Rp_Id ? 'selected' : ''}`} title={rp.Rp_Nombre} onClick={() => selectReport(rp)}>
                          {getReportIcon(rp.Rp_Nombre) === 'microscopie' ? <img src={microscopieBlue} alt='microscopio' /> : <i className={`dx-icon dx-icon-${getReportIcon(rp.Rp_Nombre)}`}></i>}
                        </button>
                      ))
                    ) : (
                      <div className='collapsed-empty'>No hay tableros</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className='boards-content'>
              {!selectedReport ? (
                <div className='empty-state'>
                  <i className='dx-icon dx-icon-detailslayout'></i>
                  <h3>Selecciona un tablero</h3>
                  <p>Elige un tablero de control de la lista para comenzar a visualizar datos</p>
                </div>
              ) : (
                <DataGrid
                  height={'calc(100vh - 200px)'}
                  allowColumnResizing={true}
                  columnResizingMode='nextColumn'
                  className='results-grid'
                  ref={gridRef}
                  onExporting={onExporting}
                  dataSource={filteredData}
                  rowAlternationEnabled={true}
                  onContentReady={(e) => {
                    // Forzar actualización cuando cambie el contenido
                    setVisibleCount(e.component.totalCount());
                  }}>
                  <LoadPanelDg enabled={true} />
                  <Export enabled={true} />
                  <ColumnChooser enabled={true} mode='select' />
                  <FilterRow visible={true} applyFilter='auto' />
                  <GroupPanel visible={true} />
                  <Scrolling mode='standard' columnRenderingMode='virtual' />
                  <SearchPanel visible placeholder='Buscar' />
                  <Paging enabled={true} />
                  <Pager visible={selectedReport} allowedPageSizes={[20, 50, 100, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />
                  <Toolbar>
                    <Item location='before' showText='always'>
                      <div className='grid-header'>
                        {selectedReport.Rp_Nombre} {gridRef.current?.instance ? `(${gridRef.current.instance.totalCount()} filas)` : ''} |
                      </div>
                    </Item>
                    {/* resto de items */}

                    <Item visible={selectedReport} location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                      <Tooltip target='.filter-button' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                        Filtrar
                      </Tooltip>
                      <Button className='filter-button' icon={filter} text='Filtrar' stylingMode='text' onClick={() => setFiltrosVisible(true)} />
                    </Item>
                    <Item location='before' visible={selectedReport} name='groupPanel' />
                    <Item visible={selectedReport} location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                      <Button id='refreshRp' onClick={handleClicRefresh} icon='refresh' text='Refrescar' stylingMode='text' />
                      <Tooltip target={'#refreshRp'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                        Refrescar datos
                      </Tooltip>
                    </Item>
                    <Item location='after' visible={selectedReport} name='exportButton' />
                    <Item location='after' visible={selectedReport} name='columnChooserButton' />
                    <Item location='after' visible={selectedReport} name='searchPanel' />
                  </Toolbar>
                </DataGrid>
              )}
            </div>
          </div>
        </ToolbarSimple>
        <FormPopupSimpleItem dataSource={dataFiltered} isSaveDisabled={false} loading={sending} onSave={handleClicFilter} title='Seleccione los filtros' visible={filtrosVisible} setVisible={handleVisibleFilter} fields={filtros} />
      </ScrollView>
    </div>
  );
};

export default Index;
